/**
 * Saved activities (TRAV23 activities-front merge).
 *
 * Separate from `@share/store/slices/saved-properties`: that slice is the hotel/condo
 * list and talks to `/identity/users/saved-property`, while activities have their own
 * collection behind `Urls.ActivitySavedProperty` ('/identity/users/saved-activity') and
 * are keyed by `activityCode` rather than `propertyId`.
 *
 * Toast copy follows the same `isSpanish()` pattern as the hotel slice rather than
 * react-intl, since these fire from thunks with no `intl` in scope.
 */

import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';

import { IActivity } from '@common-types';
import { Urls } from '@share/constants';
import { axiosInstanceB2B, getHeaders, isSpanish, Toaster } from '@share/utils';

const OK_STATUS = 200;

export interface IActivitySavedPropertyPayload {
  activityCode: string | number;
  address: string;
  imgSrc: string;
  name: string;
  rating: number;
  reviewsCount: number;
  type: string;
}

export interface IActivitySavedPropertyState {
  loading: boolean;
  error: string | null;
  savedIds: (string | number)[];
  savedProperties: IActivitySavedPropertyPayload[];
}

const initialState: IActivitySavedPropertyState = {
  loading: false,
  error: null,
  savedIds: [],
  savedProperties: [],
};

interface ISaveActivityArgs {
  payload: IActivitySavedPropertyPayload;
  /** State *before* the click: true means this call removes the activity. */
  isSaved: boolean;
}

export const saveActivity = createAsyncThunk<any, ISaveActivityArgs>(
  'activitySavedProperty/saveActivity',
  async ({ payload, isSaved }, { rejectWithValue }) => {
    try {
      const response = await axiosInstanceB2B.post(
        Urls.ActivitySavedProperty,
        payload,
        getHeaders(),
      );

      if (response.status === OK_STATUS) {
        if (!isSaved) {
          Toaster.success(
            !isSpanish() ? 'Activity saved successfully' : 'Actividad guardada con éxito',
          );
        } else {
          Toaster.success(
            !isSpanish()
              ? 'Saved Activity removed successfully'
              : 'Actividad guardada eliminada con éxito',
          );
        }
      }

      return { id: payload.activityCode, data: response.data, payload, isSaved };
    } catch (error: any) {
      Toaster.error(
        !isSpanish() ? 'Failed to save activity' : 'No se pudo guardar la actividad',
      );

      return rejectWithValue(error.response?.data || 'Failed to save activity');
    }
  },
);

export const getSavedActivities = createAsyncThunk(
  'activitySavedProperty/getSavedActivities',
  async (params: { orderBy?: string; sortOrder?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await axiosInstanceB2B.get(Urls.ActivitySavedProperty, {
        ...getHeaders(),
        params,
      });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch saved activities');
    }
  },
);

export const removeAllSavedActivities = createAsyncThunk(
  'activitySavedProperty/removeAllSavedActivities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstanceB2B.delete(Urls.ActivitySavedProperty, getHeaders());

      if (response.status === OK_STATUS) {
        Toaster.success(
          !isSpanish()
            ? 'All saved activities removed successfully'
            : 'Todas las actividades guardadas eliminadas con éxito',
        );
      }

      return response.data;
    } catch (error: any) {
      Toaster.error(
        !isSpanish()
          ? 'Failed to remove all saved activities'
          : 'No se pudieron eliminar todas las actividades guardadas',
      );

      return rejectWithValue(error.response?.data || 'Failed to remove all saved activities');
    }
  },
);

export const removeSavedActivity = createAsyncThunk(
  'activitySavedProperty/removeSavedActivity',
  async (
    { propertyId, propertyType }: { propertyId: string | number; propertyType: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstanceB2B.post(
        Urls.ActivitySavedProperty,
        { propertyId, propertyType },
        getHeaders(),
      );

      if (response.status === OK_STATUS) {
        Toaster.success(
          !isSpanish()
            ? 'Saved Activity removed successfully'
            : 'Actividad guardada eliminada con éxito',
        );
      }

      return propertyId;
    } catch (error: any) {
      Toaster.error(
        !isSpanish()
          ? 'Failed to remove saved activity'
          : 'No se pudo eliminar la actividad guardada',
      );

      return rejectWithValue(error.response?.data || 'Failed to remove saved activity');
    }
  },
);

const activitySavedPropertySlice = createSlice({
  name: 'activitySavedProperty',
  initialState,
  reducers: {
    /** Optimistic flip so the heart responds before the request lands. */
    toggleLocalSaved: (state, { payload }: PayloadAction<string | number>) => {
      const index = state.savedIds.indexOf(payload);

      if (index > -1) {
        state.savedIds.splice(index, 1);
      } else {
        state.savedIds.push(payload);
      }
    },
    /**
     * Reconciles saved state from a search or detail response. `savedActivity` is
     * tri-state: `undefined` means the response carries no opinion, so leave it alone.
     */
    syncSavedIds: (state, { payload }: PayloadAction<IActivity[] | IActivity>) => {
      const activities = Array.isArray(payload) ? payload : [payload];

      activities.forEach((activity) => {
        if (activity?.savedActivity === true) {
          if (!state.savedIds.includes(activity.code)) {
            state.savedIds.push(activity.code);
          }
        } else if (activity?.savedActivity === false) {
          state.savedIds = state.savedIds.filter((id) => id !== activity.code);
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveActivity.fulfilled, (state, action) => {
        state.loading = false;

        if (!state.savedIds.includes(action.payload.id)) {
          if (!action.payload.isSaved) {
            state.savedIds.push(action.payload.id);
            state.savedProperties.unshift(action.payload.payload);
          } else {
            state.savedIds = state.savedIds.filter((id) => id !== action.payload.id);
            state.savedProperties = state.savedProperties.filter(
              (item) => item.activityCode !== action.payload.id,
            );
          }
        }
      })
      .addCase(saveActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(getSavedActivities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSavedActivities.fulfilled, (state, action) => {
        state.loading = false;
        // The endpoint has shipped three envelope shapes; accept all of them.
        state.savedProperties = (action.payload.result ||
          action.payload.items ||
          action.payload ||
          []) as IActivitySavedPropertyPayload[];
        state.savedIds = state.savedProperties.map(
          (property) =>
            property.activityCode || (property as any).foreignId || (property as any).id,
        );
      })
      .addCase(getSavedActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(removeSavedActivity.fulfilled, (state, action) => {
        state.savedIds = state.savedIds.filter((id) => id !== action.payload);
        state.savedProperties = state.savedProperties.filter(
          (property) =>
            property.activityCode !== action.payload &&
            (property as any).foreignId !== action.payload &&
            (property as any).id !== action.payload,
        );
      })

      .addCase(removeAllSavedActivities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeAllSavedActivities.fulfilled, (state) => {
        state.loading = false;
        state.savedIds = [];
        state.savedProperties = [];
      })
      .addCase(removeAllSavedActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const activitySavedPropertyActions = {
  ...activitySavedPropertySlice.actions,
  saveActivity,
  getSavedActivities,
  removeSavedActivity,
  removeAllSavedActivities,
};

export const activitySavedPropertyReducer = activitySavedPropertySlice.reducer;
